import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { FormSubmissionStatus, FormSubmissionPurpose, FormField, FormFieldType, FormTemplate } from '@/core/models/form.model'
import { useGetFormTemplateQuery, useGetFormSubmissionQuery, useCreateFormSubmissionMutation, useUpdateFormSubmissionMutation } from '@/core/services/form'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import FormSubmissionComponent from '../components/FormSubmission'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

export default function FormSubmissionContainer() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const objectType = searchParams.get('type') // 'consultation', 'expertise' or 'patient'
  const objectId = searchParams.get('objectId') // consultation or expertise ID
  const purpose = searchParams.get('purpose') // e.g. 'readiness-checklist' — tags special-purpose submissions
  const authUser = useAppSelector(selectAuthUser)
  const { templateId, patientId, submissionId } = useParams<{
    templateId: string
    patientId: string
    submissionId?: string
  }>()

  const { data: template, isLoading: isLoadingTemplate } = useGetFormTemplateQuery(templateId ?? skipToken)

  const { data: existingSubmission, isLoading: isLoadingSubmission } = useGetFormSubmissionQuery(submissionId!, { skip: !submissionId })

  const [createSubmission] = useCreateFormSubmissionMutation()
  const [updateSubmission] = useUpdateFormSubmissionMutation()

  const [formData, setFormData] = React.useState<Record<string, any>>({})
  const [autoSaveStatus, setAutoSaveStatus] = useState<'saving' | 'saved' | 'error'>('saved')
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [submissionInProgress, setSubmissionInProgress] = useState(false)

  const { t } = useTranslation()

  useEffect(() => {
    if (existingSubmission) {
      const initialData: Record<string, any> = {}
      existingSubmission.responses.forEach((response) => {
        initialData[response.fieldId] = response.value
      })
      setFormData(initialData)
    }
  }, [existingSubmission])

  const getResponses = useCallback((data: Record<string, any>) => {
    return Object.entries(data)
      .filter(([_, value]) => value != null)
      .map(([fieldId, value]) => ({
        fieldId,
        value,
      }))
  }, [])

  const handleChange = useCallback((data: Record<string, any>) => {
    setFormData(data)
    setValidationErrors({})
  }, [])

  const validateForm = useCallback(() => {
    if (!template) return false

    const errors: Record<string, string> = {}
    template.fields.forEach((field: FormField) => {
      if (field.type === FormFieldType.Boolean) {
        formData[field.id] = formData[field.id] ?? false
        return
      }

      if (field.required && (!formData[field.id] || formData[field.id] === '')) {
        errors[field.id] = t('page.forms.validation.required')
      }
    })

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }, [template, formData, t])

  const handleSubmit = useCallback(() => {
    if (!validateForm()) return
    setIsConfirmDialogOpen(true)
  }, [validateForm])

  const handleConfirmSubmit = useCallback(async () => {
    if (!template || !authUser) return

    try {
      setSubmissionInProgress(true)
      const responses = getResponses(formData)

      if (submissionId) {
        await updateSubmission({
          id: submissionId,
          changes: {
            responses,
            status: FormSubmissionStatus.Submitted,
          },
        }).unwrap()
      } else {
        const submissionData: {
          template: string
          patient: string
          responses: typeof responses
          status: FormSubmissionStatus
          expertise?: string
          consultation?: string
          purpose?: FormSubmissionPurpose
        } = {
          template: templateId!,
          patient: patientId!,
          responses,
          status: FormSubmissionStatus.Submitted,
        }

        // Only add expertise or consultation if objectId is present
        if (objectId && objectType !== 'patient') {
          if (objectType === 'expertise') {
            submissionData.expertise = objectId
          } else {
            submissionData.consultation = objectId
          }
        }

        // Tag special-purpose submissions (e.g. waiting-room readiness checklist) so feature code finds them.
        if (purpose) submissionData.purpose = purpose as FormSubmissionPurpose

        await createSubmission(submissionData).unwrap()
      }

      if (window.opener) {
        window.close()
      } else {
        if (objectType === 'expertise' && objectId) {
          navigate(`/expertises/${objectId}`)
        } else if (objectType === 'consultation' && objectId) {
          navigate(`/diagnostic/local/${objectId}`)
        } else if (objectType === 'patient' && patientId) {
          navigate(`/patients/${patientId}`)
        } else {
          navigate('/')
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error)
      setValidationErrors({})
    } finally {
      setSubmissionInProgress(false)
      setIsConfirmDialogOpen(false)
    }
  }, [template, authUser, formData, submissionId, templateId, patientId, objectId, objectType, navigate, getResponses, updateSubmission, createSubmission])

  const handleCloseConfirmDialog = useCallback(() => {
    setIsConfirmDialogOpen(false)
  }, [])

  if (isLoadingTemplate || (submissionId && isLoadingSubmission)) {
    return <div>Loading...</div>
  }

  if (!template) {
    return <div>Template not found</div>
  }

  return (
    <FormSubmissionComponent
      template={template}
      formData={formData}
      onFormChange={handleChange}
      onSubmit={handleSubmit}
      autoSaveStatus={autoSaveStatus}
      validationErrors={validationErrors}
      isConfirmDialogOpen={isConfirmDialogOpen}
      submissionInProgress={submissionInProgress}
      onConfirmDialogClose={handleCloseConfirmDialog}
      onConfirmSubmit={handleConfirmSubmit}
    />
  )
}